import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { ErrorFallback, buildNotification, GenerateNotification } from 'smart-react';
import { ErrorBoundary } from 'react-error-boundary';
import { Archive, Download, FileUp, Trash2 } from 'lucide-react';

import Loader from '../../Core/Components/Loader/Loader';
import { generateRollout } from '../Services/GenerateRolloutService';
import { EVENTS_DATA_TYPES, NOTIFICATION_TYPES } from '../../../constants/eventDataTypes';
import { Button } from '@/Modules/Core/Components/shadcn/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/Modules/Core/Components/shadcn/card';
import { Badge } from '@/Modules/Core/Components/shadcn/badge';
import { AppPageSection } from '@/Modules/Core/Components/Layout/AppPage';
import { cn } from '@/lib/utils';

const GenerateRollout = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('zip');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/zip': ['.zip'],
      'application/x-tar': ['.tar'],
    },
    maxFiles: 1,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        GenerateNotification(
          buildNotification({
            title: 'Invalid file type',
            description: 'Only .zip and .tar files are allowed.',
            style: 'error',
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
        );
      } else if (acceptedFiles.length > 0) {
        setDownloadUrl('');
        setFile(acceptedFiles[0]);
      }
    },
  });

  const handleProcess = async () => {
    if (!file) return;

    setIsProcessing(true);

    try {
      const data = await generateRollout(file, fileType);
      if (data.isSuccess) {
        setDownloadUrl(data.downloadUrl);
        setFile(null);
        GenerateNotification(
          buildNotification({
            title: 'Rollout successfully generated',
            style: 'success',
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
        );
      } else {
        GenerateNotification(
          buildNotification({
            title: 'Error generating rollout',
            description: data.message,
            style: 'error',
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
        );
      }
    } catch (error) {
      console.error('Error processing file:', error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    window.location.href = downloadUrl;
  };

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <React.Suspense fallback={<Loader />}>
        {loading && <Loader />}
        <div className="tw">
          <div className="space-y-6">
            <AppPageSection>
              <Card className="upload-surface">
                <CardHeader className="border-b border-border/50 pb-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="secondary" className="rounded-full px-3 py-1 text-[11px]">
                          Archive Processing
                        </Badge>
                        <Badge variant="outline" className="rounded-full px-3 py-1 text-[11px]">
                          {fileType.toUpperCase()} output
                        </Badge>
                      </div>
                      <CardTitle className="mt-3 text-[20px] font-semibold text-foreground">
                        Generate a rollout from an archive
                      </CardTitle>
                      <CardDescription className="mt-1 text-[13px] leading-6 text-muted-foreground">
                        Upload a `.zip` or `.tar` archive that contains the files you
                        want to process, then generate a rollout artifact using the
                        standardized utility flow.
                      </CardDescription>
                    </div>

                    <div className="inline-flex items-center rounded-md border border-border/60 bg-muted/20 p-1">
                      {['zip', 'tar'].map((type) => (
                        <Button
                          key={type}
                          type="button"
                          size="sm"
                          variant="ghost"
                          className={cn(
                            'rounded-md px-3 uppercase',
                            fileType === type
                              ? 'bg-background text-foreground shadow-sm'
                              : 'text-muted-foreground'
                          )}
                          onClick={() => setFileType(type)}
                        >
                          {type}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 px-5 py-5">
                  <div
                    {...getRootProps({
                      className: cn(
                        'dropzone cursor-pointer',
                        isDragActive && 'border-primary bg-primary/5'
                      ),
                    })}
                  >
                    <input {...getInputProps()} />
                    <div className="space-y-3 text-center">
                      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                        <FileUp className="h-6 w-6" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-[14px] font-medium text-foreground">
                          Drag and drop an archive here, or click to select one
                        </p>
                        <p className="text-[12px] text-muted-foreground">
                          Accepted formats: `.zip` and `.tar`
                        </p>
                      </div>
                    </div>
                  </div>

                  {file ? (
                    <div className="file-info">
                      <p>
                        <strong>Selected file:</strong> {file.name}
                      </p>
                      <p>
                        <strong>Size:</strong>{' '}
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setFile(null)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Remove File
                        </Button>
                      </div>
                    </div>
                  ) : null}

                  {downloadUrl ? (
                    <div className="rounded-xl border border-primary/20 bg-primary/10 px-4 py-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-background text-primary">
                            <Archive className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="text-[13px] font-medium text-foreground">
                              Rollout is ready to download
                            </p>
                            <p className="text-[12px] leading-6 text-muted-foreground">
                              The generated archive is available now.
                            </p>
                          </div>
                        </div>

                        <Button type="button" onClick={handleDownload}>
                          <Download className="mr-2 h-4 w-4" />
                          Download
                        </Button>
                      </div>
                    </div>
                  ) : null}

                  <div className="process-button-container">
                    <Button
                      type="button"
                      onClick={handleProcess}
                      disabled={isProcessing || !file}
                    >
                      {isProcessing ? 'Processing...' : 'Process Archive'}
                    </Button>
                  </div>
                  {isProcessing ? <Loader /> : null}
                </CardContent>
              </Card>
            </AppPageSection>
          </div>
        </div>
      </React.Suspense>
    </ErrorBoundary>
  );
};

export default GenerateRollout;
